<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('catering_request_form')"
                        :isFilter="false"/>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('catering_request_form')"
                              :isFilter="false"/>
            </template>
            <div v-if="requesterInfo">
                <div class="row data-form">
                    <div class="col-12">
                        <div class="mb-4">
                            <h6 class="text-uppercase mb-0">{{ $t('information').toUpper() }}</h6>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('name')">{{ requesterInfo.name }}</b-form-group>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('surname')">{{ requesterInfo.surname }}</b-form-group>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('employee_id')">{{ requesterInfo.employee_id }}</b-form-group>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('job_title')">{{ requesterInfo.job_title }}</b-form-group>
                    </div>
                </div>
                <hr>
                <div class="my-3 d-inline-block"></div>
                <div class="mb-4">
                    <h6 class="text-uppercase mb-0">{{ $t('application_information').toUpper() }}</h6>
                </div>
                <ValidationObserver ref="cateringForm">
                    <b-row>
                        <!-- Event -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="event" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('event')">
                                    <multiselect
                                        :placeholder="$t('select')"
                                        v-model="selectedEvent"
                                        :options="optionsData"
                                        label="name"
                                        track-by="code"
                                        :select-label="''"
                                        :selected-label="''"
                                        :deselect-label="''"
                                        @input="onEventChange"
                                    />
                                    <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>

                        <!-- Event Specify -->
                        <b-col cols="12" md="6" v-if="selectedEvent && selectedEvent.requires_specification">
                            <ValidationProvider name="event_specify" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('event_specify')">
                                    <b-form-input
                                        v-model="formData.event_specify"
                                        :class="errors[0] ? 'is-invalid' : ''"
                                        maxlength="255"
                                    />
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>

                        <!-- Account Number (Readonly) -->
                        <b-col cols="12" md="6" v-if="selectedEvent">
                            <b-form-group :label="$t('account_number')">
                                <b-form-input
                                    :value="selectedEvent.account_number || '-'"
                                    readonly
                                    disabled
                                />
                            </b-form-group>
                        </b-col>

                        <!-- Account Name (Readonly) -->
                        <b-col cols="12" md="6" v-if="selectedEvent">
                            <b-form-group :label="$t('account_name')">
                                <b-form-input
                                    :value="selectedEvent.account_name || '-'"
                                    readonly
                                    disabled
                                />
                            </b-form-group>
                        </b-col>

                        <!-- Audience -->
                        <b-col cols="12" md="6" v-if="selectedEvent">
                            <ValidationProvider name="audience" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('audience')">
                                    <multiselect
                                        :placeholder="$t('select')"
                                        v-model="selectedAudience"
                                        :options="selectedEvent.audiences || []"
                                        label="name"
                                        track-by="code"
                                        :select-label="''"
                                        :selected-label="''"
                                        :deselect-label="''"
                                        @input="onAudienceChange"
                                    />
                                    <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>

                        <!-- Audience Specify -->
                        <b-col cols="12" md="6" v-if="selectedAudience && selectedAudience.requires_specification">
                            <ValidationProvider name="audience_specify" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('audience_specify')">
                                    <b-form-input
                                        v-model="formData.audience_specify"
                                        :class="errors[0] ? 'is-invalid' : ''"
                                        maxlength="255"
                                    />
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>

                        <!-- Class -->
                        <b-col cols="12" md="6" v-if="selectedAudience">
                            <!-- Custom Class Input -->
                            <ValidationProvider
                                v-if="selectedAudience.allows_custom_class"
                                name="class"
                                rules="required"
                                v-slot="{errors}"
                            >
                                <b-form-group :label="$t('class')">
                                    <b-form-input
                                        v-model="formData.class"
                                        :class="errors[0] ? 'is-invalid' : ''"
                                        maxlength="255"
                                    />
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>

                            <!-- Auto-filled / Readonly Class -->
                            <b-form-group :label="$t('class')" v-else>
                                <b-form-input
                                    :value="selectedAudience.class ? (selectedAudience.class.name || selectedAudience.class.code) : '-'"
                                    readonly
                                    disabled
                                />
                            </b-form-group>
                        </b-col>

                        <!-- Location (Financial / QuickBooks Mapping) -->
                        <b-col cols="12" md="6" v-if="selectedAudience">
                            <!-- Custom Location Input -->
                            <ValidationProvider
                                v-if="selectedAudience.allows_custom_location"
                                name="location"
                                rules="required"
                                v-slot="{errors}"
                            >
                                <b-form-group :label="$t('location')">
                                    <b-form-input
                                        v-model="formData.location"
                                        :class="errors[0] ? 'is-invalid' : ''"
                                        maxlength="255"
                                    />
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>

                            <!-- Dropdown / Auto-selected Location -->
                            <ValidationProvider
                                v-else
                                name="location"
                                rules="required"
                                v-slot="{errors}"
                            >
                                <b-form-group :label="$t('location')">
                                    <multiselect
                                        :placeholder="$t('select')"
                                        v-model="selectedLocation"
                                        :options="selectedAudience.locations || []"
                                        label="name"
                                        track-by="code"
                                        :select-label="''"
                                        :selected-label="''"
                                        :deselect-label="''"
                                        @input="onLocationChange"
                                    />
                                    <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>

                        <!-- Event Location (Physical Venue) -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="event_location" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('event_location')">
                                    <b-form-input v-model="formData.event_location"
                                                  :class="errors[0] ? 'is-invalid':''"
                                                  maxlength="255"/>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <!-- Event Date -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="event_date" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('event_date')">
                                    <select-date v-model="formData.event_date"
                                                 :validation-error="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <!-- Event Time -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="event_time" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('event_time')">
                                    <b-form-input v-model="formData.event_time"
                                                  type="time"
                                                  :class="errors[0] ? 'is-invalid':''"/>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <!-- Catering Setup Time -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="catering_setup_time" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('catering_setup_time')">
                                    <b-form-input v-model="formData.catering_setup_time"
                                                  type="time"
                                                  :class="errors[0] ? 'is-invalid':''"/>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <!-- Guest Count -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="guest_count" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('guest_count')">
                                    <b-form-input v-model="formData.guest_count"
                                                  type="number"
                                                  min="1"
                                                  :class="errors[0] ? 'is-invalid':''"/>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <!-- Budget -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="budget" rules="" v-slot="{valid, errors}">
                                <b-form-group :label="$t('budget')">
                                    <b-form-input v-model="formData.budget"
                                                  type="number"
                                                  min="0"
                                                  :class="errors[0] ? 'is-invalid':''"/>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <!-- Dish Type -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="dish_type" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('dish_type')">
                                    <parameter-selectbox code="catering_dish_types"
                                                         v-model="formData.dish_type"
                                                         :validate-error="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <!-- Dietary Restrictions -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="dietary_restrictions" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('dietary_restrictions')">
                                    <b-form-textarea v-model="formData.dietary_restrictions"
                                                     :class="errors[0] ? 'is-invalid':''"
                                                     rows="3"
                                                     maxlength="1000"/>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <!-- Requester Notes -->
                        <b-col cols="12" md="6">
                            <ValidationProvider name="requester_notes" rules="" v-slot="{valid, errors}">
                                <b-form-group :label="$t('requester_notes')">
                                    <b-form-textarea v-model="formData.requester_notes"
                                                     :class="errors[0] ? 'is-invalid':''"
                                                     rows="3"
                                                     maxlength="2000"/>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                    </b-row>

                    <!-- Menu Items -->
                    <hr>
                    <div class="mb-4">
                        <h6 class="text-uppercase mb-0">{{ $t('menu_items').toUpper() }}</h6>
                    </div>
                    <div v-for="(item, index) in formData.menu_items" :key="index" class="mb-3">
                        <b-row>
                            <b-col cols="12" md="5">
                                <ValidationProvider :name="'menu_items.' + index + '.name'" rules="required" v-slot="{valid, errors}">
                                    <b-form-group :label="$t('item_name')">
                                        <b-form-input v-model="item.name"
                                                      :class="errors[0] ? 'is-invalid':''"
                                                      maxlength="255"/>
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>
                            <b-col cols="12" md="5">
                                <ValidationProvider :name="'menu_items.' + index + '.quantity'" rules="required" v-slot="{valid, errors}">
                                    <b-form-group :label="$t('quantity')">
                                        <b-form-input v-model="item.quantity"
                                                      type="number"
                                                      min="1"
                                                      :class="errors[0] ? 'is-invalid':''"/>
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>
                            <b-col cols="12" md="2" class="d-flex align-items-center">
                                <b-button variant="danger" size="sm"
                                          :disabled="formData.menu_items.length === 1"
                                          @click="removeMenuItem(index)">
                                    <i class="ri-delete-bin-line"></i>
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                    <b-button variant="outline-primary" size="sm" @click="addMenuItem" class="mb-4">
                        <i class="ri-add-line"></i> {{ $t('add_item') }}
                    </b-button>
                </ValidationObserver>
                <div class="d-flex justify-content-center">
                    <b-form-group>
                        <b-button variant="primary" @click="save">{{ $t('save') }}</b-button>
                    </b-form-group>
                </div>
            </div>
            <div v-else>
                <div class="message-box" v-if="message && !showSuccessMessage">
                    <b-alert show variant="info" class="d-flex align-items-center">
                        <b-icon icon="info-circle"></b-icon>
                        <div class="alert-content ml-2">
                            <p class="mb-0 font-weight-bold">{{ $t('api.' + message) }}</p>
                        </div>
                    </b-alert>
                </div>
            </div>
        </app-layout>
    </div>
</template>
<script>
// Template
import AppLayout from '@/layouts/AppLayout';
import Header from '@/layouts/AppLayout/Header';
import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

// Other
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import SelectDate from "@/components/interactive-fields/SelectDate.vue";
import CateringRequestService from "@/services/CateringRequestService";

export default {
    components: {
        AppLayout,
        Header,
        HeaderMobile,

        ValidationProvider,
        ValidationObserver,
        SelectDate,
    },
    data() {
        return {
            formData: {
                event: null,
                event_specify: null,
                audience: null,
                audience_specify: null,
                class: null,
                location: null,
                event_location: null,
                event_date: null,
                event_time: null,
                catering_setup_time: null,
                guest_count: null,
                budget: null,
                dietary_restrictions: null,
                dish_type: null,
                requester_notes: null,
                menu_items: [
                    {name: '', quantity: 1}
                ],
            },
            optionsData: [],
            selectedEvent: null,
            selectedAudience: null,
            selectedLocation: null,
            requesterInfo: null,
            showSuccessMessage: false,
            message: null,
        }
    },
    metaInfo() {
        return {
            title: this.$t('catering_request_form')
        }
    },
    methods: {
        async getRequesterInfo() {
            try {
                const response = await CateringRequestService.getRequesterInfo()
                this.requesterInfo = response.data.data
            } catch (error) {
                this.message = error.data.message
                this.showErrors(error)
            }
        },
        async getOptions() {
            try {
                const response = await CateringRequestService.getOptions()
                this.optionsData = (response.data.data || []).sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
            } catch (error) {
                this.showErrors(error)
            }
        },
        onEventChange(val) {
            this.selectedEvent = val
            this.formData.event = val ? val.code : null
            this.formData.event_specify = null

            // Reset audience, audience_specify, class, location
            this.selectedAudience = null
            this.formData.audience = null
            this.formData.audience_specify = null
            this.formData.class = null
            this.selectedLocation = null
            this.formData.location = null

            // Auto-select audience if only 1 option
            if (val && val.audiences && val.audiences.length === 1) {
                this.onAudienceChange(val.audiences[0])
            }
        },
        onAudienceChange(val) {
            this.selectedAudience = val
            this.formData.audience = val ? val.code : null
            this.formData.audience_specify = null
            this.formData.class = null
            this.selectedLocation = null
            this.formData.location = null

            if (val) {
                // Set class
                if (val.class) {
                    this.formData.class = typeof val.class === 'object' ? val.class.code : val.class
                }

                // Set locations
                if (!val.allows_custom_location && val.locations && val.locations.length === 1) {
                    this.onLocationChange(val.locations[0])
                }
            }
        },
        onLocationChange(val) {
            this.selectedLocation = val
            this.formData.location = val ? val.code : null
        },
        addMenuItem() {
            this.formData.menu_items.push({name: '', quantity: 1})
        },
        removeMenuItem(index) {
            this.formData.menu_items.splice(index, 1)
        },
        async save() {
            if (!await this.$refs.cateringForm.validate()) return
            try {
                const payload = {
                    event: this.formData.event,
                    event_specify: (this.selectedEvent && this.selectedEvent.requires_specification) ? this.formData.event_specify : null,
                    audience: this.formData.audience,
                    audience_specify: (this.selectedAudience && this.selectedAudience.requires_specification) ? this.formData.audience_specify : null,
                    class: this.formData.class,
                    location: this.formData.location,
                    event_location: this.formData.event_location,
                    event_date: this.formData.event_date,
                    event_time: this.formData.event_time,
                    catering_setup_time: this.formData.catering_setup_time,
                    guest_count: this.formData.guest_count,
                    budget: this.formData.budget,
                    dish_type: this.formData.dish_type,
                    dietary_restrictions: this.formData.dietary_restrictions,
                    requester_notes: this.formData.requester_notes,
                    menu_items: this.formData.menu_items,
                }
                const res = await CateringRequestService.store(payload)
                this.$toast.success(this.$t(res.data.message))
                this.$router.push('/my-staff-requests/0')
            } catch (error) {
                this.showErrors(error, this.$refs.cateringForm)
            }
        }
    },
    async created() {
        await Promise.all([
            this.getRequesterInfo(),
            this.getOptions(),
        ])
    },
};
</script>
